/**
 * Ledger tab (redesign 2026-10-05, owner's brief): the default is a
 * TODAY SUMMARY only — tiles + a compact "By action" table, from the
 * server-side summary. A period selector (Today / Yesterday / Last 7 days /
 * This month / Custom range) drives everything; the optional merchant
 * type-ahead narrows it. The transactions list is opened explicitly
 * ("Show runs for <period>") and pages 25 at a time, newest first.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, List, X } from 'lucide-react';
import { RubyLoader } from '../../../components/RubyLoader';
import { RubyGemMark } from '../../../components/RubyGemMark';
import {
  exportRubyLedgerUrl, getRubyLedgerSummary,
  type LedgerFilters, type LedgerSummaryResponse,
} from '../../../services/aiControl/ledger';
import { getRubyAiCatalog, type AiCatalogResponse } from '../../../services/aiControl/rubyAi';
import { money, periodPhrase, ratingCounts, resolvePeriod, type LedgerPeriod } from './ledgerModel';
import { formatRubyTokens, tokensLabel } from '../rubyAi/units';
import { ErrorBanner } from '../rubyAi/ui';
import type { AiTabProps } from '../AiControlCenter';
import LedgerPeriodPicker from './LedgerPeriodPicker';
import LedgerMerchantSearch, { type MerchantValue } from './LedgerMerchantSearch';
import LedgerRuns, { EMPTY_RUN_FILTERS, type RunFilters } from './LedgerRuns';
import { DeveloperSpend } from './DeveloperSpend';

/** Wait this long after the last keystroke before a typed filter re-queries. */
const TYPE_DEBOUNCE_MS = 350;

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const LedgerTab: React.FC<AiTabProps> = ({ tenantId: deepLinkTenantId, onHeaderStats, showToast }) => {
  const [period, setPeriod] = useState<LedgerPeriod>({ preset: 'today' });
  // Resolved once per period choice (Refresh in the shell remounts the tab → a fresh "now").
  const periodWindow = useMemo(() => resolvePeriod(period), [period]);

  const [merchant, setMerchant] = useState<MerchantValue>(
    deepLinkTenantId ? { text: deepLinkTenantId, tenantId: deepLinkTenantId } : { text: '' },
  );
  const merchantTyped = useDebounced(merchant.text, TYPE_DEBOUNCE_MS);
  const tenantFilter = (merchant.tenantId ?? merchantTyped.trim()) || undefined;

  const [catalog, setCatalog] = useState<AiCatalogResponse | null>(null);
  useEffect(() => {
    let live = true;
    getRubyAiCatalog().then((c) => { if (live) setCatalog(c); }, () => { /* labels fall back to action ids */ });
    return () => { live = false; };
  }, []);

  // ---- Summary (always shown) ----
  /*
    THE VIEW. Same ledger, different lens: everything, merchants' AI only, or
    the issue console's developer spend (lines tagged params.kind='developer').
  */
  const [view, setView] = useState<'all' | 'customer' | 'developer'>('all');
  const [whoInput, setWhoInput] = useState('');
  const who = useDebounced(whoInput, TYPE_DEBOUNCE_MS);
  const [devProfile, setDevProfile] = useState('');
  const [devCost, setDevCost] = useState<'' | 'actual' | 'estimated'>('');
  const viewFilters: Partial<LedgerFilters> = useMemo(() => (
    view === 'developer'
      ? { kind: 'developer', who: who.trim() || undefined, profileId: devProfile || undefined, cost: devCost || undefined }
      : view === 'customer' ? { kind: 'customer' } : {}
  ), [view, who, devProfile, devCost]);
  const viewKey = JSON.stringify(viewFilters);

  const [summary, setSummary] = useState<LedgerSummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setSummaryLoading(true);
    setSummaryError(null);
    getRubyLedgerSummary({ fromIso: periodWindow.fromIso, toIso: periodWindow.toIso, tenantId: tenantFilter, ...viewFilters }).then((s) => {
      if (!live) return;
      setSummary(s);
      const t = s.totals;
      onHeaderStats([
        { label: 'Runs', value: String(t?.runs ?? 0), tone: 'white' },
        { label: 'Charged', value: tokensLabel(t?.tokensCharged ?? 0), tone: 'blue' },
        { label: 'Profit', value: money(t?.profitUsd ?? 0), tone: (t?.profitUsd ?? 0) >= 0 ? 'emerald' : 'rose' },
        { label: 'Refunded', value: String(t?.refundedRuns ?? 0), tone: 'amber' },
      ]);
    }, (err: any) => {
      if (live) setSummaryError(err?.message || 'Failed to load the ledger summary');
    }).finally(() => { if (live) setSummaryLoading(false); });
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodWindow.fromIso, periodWindow.toIso, tenantFilter, onHeaderStats, viewKey]);

  // ---- Transactions (opened explicitly, per period) ----
  const [showRuns, setShowRuns] = useState(false);
  const [runFilters, setRunFilters] = useState<RunFilters>(EMPTY_RUN_FILTERS);
  const [itemInput, setItemInput] = useState('');
  const itemFilter = useDebounced(itemInput, TYPE_DEBOUNCE_MS);
  const effectiveRunFilters = useMemo(() => ({ ...runFilters, item: itemFilter }), [runFilters, itemFilter]);

  const choosePeriod = useCallback((p: LedgerPeriod) => {
    setPeriod(p);
    // The list belongs to the period it was opened for.
    if (p.preset !== period.preset) setShowRuns(false);
  }, [period.preset]);

  // ---- Export (period + merchant + list filters) ----
  const [exporting, setExporting] = useState(false);
  const [exportNotice, setExportNotice] = useState<string | null>(null);
  const exportFilters: LedgerFilters = useMemo(() => ({
    fromIso: periodWindow.fromIso,
    toIso: periodWindow.toIso,
    tenantId: tenantFilter,
    ...viewFilters,
    ...(showRuns ? {
      status: effectiveRunFilters.status || undefined,
      rating: (effectiveRunFilters.rating || undefined) as LedgerFilters['rating'],
      actionId: effectiveRunFilters.actionId || undefined,
      modelId: effectiveRunFilters.modelId || undefined,
      item: effectiveRunFilters.item.trim() || undefined,
    } : {}),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [periodWindow, tenantFilter, showRuns, effectiveRunFilters, viewKey]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setExportNotice(null);
    try {
      const { blob, truncated } = await exportRubyLedgerUrl(exportFilters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ruby-ledger-${periodWindow.fromIso.slice(0, 10)}-${periodWindow.toIso.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportNotice(truncated ? 'Export stopped at 10,000 runs — narrow the date range to get the rest.' : null);
    } catch (err: any) {
      showToast(err?.message || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  }, [exportFilters, periodWindow, showToast]);

  const phrase = periodPhrase(period);
  const t = summary?.totals;
  const actionName = useMemo(() => {
    const m = new Map((catalog?.actions ?? []).map((a) => [a.actionId, a.label || a.actionId]));
    return (id: string) => m.get(id) ?? id;
  }, [catalog]);

  return (
    <>
      {/* Controls: period · merchant · export */}
      <div className="bg-white border border-stone-200 rounded-xl px-3 md:px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-stone-900">
        <LedgerPeriodPicker value={period} onChange={choosePeriod} />
        <div role="group" aria-label="Ledger view" className="inline-flex rounded-lg border border-stone-200 p-0.5 bg-stone-50">
          {([['all', 'All'], ['customer', 'Customers'], ['developer', 'Developers']] as const).map(([k, label]) => (
            <button
              key={k}
              type="button"
              aria-pressed={view === k}
              onClick={() => { setView(k); setDevProfile(''); setDevCost(''); setWhoInput(''); }}
              className={`min-h-[44px] px-3 rounded-md text-[12px] font-semibold ${view === k ? 'bg-white text-[#9F1239] shadow-sm' : 'text-stone-600 hover:text-stone-900'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto lg:ml-auto">
          <LedgerMerchantSearch value={merchant} onChange={setMerchant} />
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-lg border border-stone-200 bg-white text-[12px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-60"
          >
            {exporting ? <RubyGemMark size={14} drawing color="currentColor" title="Exporting" /> : <Download size={14} className="text-stone-500" />} Export CSV
          </button>
        </div>
      </div>

      {exportNotice && (
        <p role="status" className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{exportNotice}</p>
      )}

      {summaryError && <ErrorBanner title="Failed to load the ledger summary" detail={summaryError} />}

      {/* Developer filters: who · profile · actual/estimated */}
      {view === 'developer' && (
        <div className="bg-white border border-stone-200 rounded-xl px-3 md:px-4 py-2.5 flex flex-wrap items-center gap-2 text-stone-900">
          <label className="flex items-center gap-2 text-[12px] text-stone-600">
            Developer
            <input
              value={whoInput}
              onChange={(e) => setWhoInput(e.target.value)}
              placeholder="Name or email"
              aria-label="Filter by developer"
              className="h-11 w-56 rounded-lg border border-stone-300 px-3 text-[13px] text-stone-900 bg-white"
            />
          </label>
          <div role="group" aria-label="Cost" className="inline-flex rounded-lg border border-stone-200 p-0.5 bg-stone-50">
            {([['', 'Any cost'], ['actual', 'Actual'], ['estimated', 'Estimated']] as const).map(([k, label]) => (
              <button key={k || 'any'} type="button" aria-pressed={devCost === k} onClick={() => setDevCost(k)}
                className={`min-h-[44px] px-3 rounded-md text-[12px] font-semibold ${devCost === k ? 'bg-white text-[#9F1239] shadow-sm' : 'text-stone-600'}`}>
                {label}
              </button>
            ))}
          </div>
          {devProfile && (
            <button type="button" onClick={() => setDevProfile('')} className="min-h-[44px] px-3 rounded-full bg-[#FFF1F3] text-[#9F1239] text-[12px] font-semibold inline-flex items-center gap-1">
              Profile: {summary?.byProfile?.find((g) => g.key === devProfile)?.label ?? devProfile} <X size={12} />
            </button>
          )}
        </div>
      )}

      {view === 'developer' && (
        <DeveloperSpend
          summary={summary}
          phrase={phrase}
          who={who}
          profileId={devProfile}
          onWho={(k) => setWhoInput(k)}
          onProfile={(k) => setDevProfile(devProfile === k ? '' : k)}
          onIssue={(k) => { setItemInput(k); setShowRuns(true); }}
        />
      )}

      {/* Summary */}
      {view !== 'developer' && (
      <section aria-label={`Summary for ${phrase}`} className="bg-white border border-stone-200 rounded-xl text-stone-900" data-testid="ledger-summary">
        <div className="px-3 md:px-4 pt-3 pb-1 flex items-baseline justify-between gap-2">
          <h2 className="text-[13px] font-semibold text-stone-900">Summary · <span className="text-stone-600 font-normal">{phrase}</span></h2>
          {summary?.partial && (
            <span className="text-[11px] text-amber-800">Partial — more than {summary.scanned?.toLocaleString('en-US') ?? '10,000'} runs; narrow the period</span>
          )}
        </div>
        <div className="relative">
          {summaryLoading && !summary ? (
            <RubyLoader variant="section" />
          ) : (
            <>
              {summaryLoading && <RubyLoader variant="overlay" />}
              <dl className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 divide-x divide-stone-100 border-b border-stone-100">
                <Tile label="Runs" value={String(t?.runs ?? 0)} />
                <Tile label="We paid vendors" value={money(t?.vendorCostUsd ?? 0)} />
                <Tile label="Ruby value" value={formatRubyTokens(t?.rubyValueTokens ?? 0)} unit="Ruby Tokens" />
                <Tile label="Charged" value={formatRubyTokens(t?.tokensCharged ?? 0)} unit="Ruby Tokens" />
                <Tile label="Profit" value={money(t?.profitUsd ?? 0)} tone={(t?.profitUsd ?? 0) < 0 ? 'text-rose-600' : 'text-emerald-700'} />
                <Tile label="Refunded runs" value={String(t?.refundedRuns ?? 0)} />
                <Tile label="Rating" value={ratingCounts({ good: t?.good ?? 0, bad: t?.bad ?? 0 })} />
                <Tile label="Free AI runs" value={String(t?.freeRuns ?? 0)} />
              </dl>

              <div className="px-3 md:px-4 py-2">
                <h3 className="text-[11px] uppercase tracking-wide text-stone-500 font-semibold py-1">By action</h3>
                {(summary?.byAction ?? []).length === 0 ? (
                  <p className="text-[12.5px] text-stone-500 py-2">No AI runs {phrase === 'today' ? 'yet today' : `for ${phrase}`}.</p>
                ) : (
                  <table className="w-full text-[12.5px] text-stone-900" data-testid="ledger-by-action">
                    <thead>
                      <tr className="text-left text-[11px] text-stone-500">
                        <th className="py-1 pr-3 font-medium text-stone-500">Action</th>
                        <th className="py-1 px-3 font-medium text-right text-stone-500">Runs</th>
                        <th className="py-1 px-3 font-medium text-right text-stone-500">Vendor $</th>
                        <th className="py-1 px-3 font-medium text-right text-stone-500">Charged</th>
                        <th className="py-1 pl-3 font-medium text-right text-stone-500">Profit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {summary!.byAction.map((a) => (
                        <tr key={a.actionId}>
                          <td className="py-1.5 pr-3 text-stone-900 truncate max-w-0 w-1/2" title={a.actionId}>{actionName(a.actionId)}</td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-stone-700">{a.runs}</td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-stone-700">{money(a.vendorCostUsd)}</td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-stone-700">{tokensLabel(a.tokensCharged)}</td>
                          <td className={`py-1.5 pl-3 text-right tabular-nums font-semibold ${a.profitUsd < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{money(a.profitUsd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </section>
      )}

      {/* Transactions — only on request. */}
      {!showRuns ? (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setShowRuns(true)}
            className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full bg-[#DC143C] text-white text-[13px] font-semibold hover:bg-[#9F1239]"
          >
            <List size={15} /> Show runs for {phrase}
          </button>
        </div>
      ) : (
        <section aria-label={`Runs for ${phrase}`} className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-[13px] font-semibold text-stone-900">Runs · <span className="text-stone-600 font-normal">{phrase}, newest first</span></h2>
            <button
              type="button"
              onClick={() => setShowRuns(false)}
              className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-lg text-[12px] font-semibold text-stone-600 hover:text-stone-900"
            >
              <X size={14} /> Hide runs
            </button>
          </div>
          <LedgerRuns
            fromIso={periodWindow.fromIso}
            toIso={periodWindow.toIso}
            tenantId={tenantFilter}
            filters={effectiveRunFilters}
            itemInput={itemInput}
            onFiltersChange={(f) => setRunFilters((cur) => ({ ...cur, ...f }))}
            onItemInput={setItemInput}
            catalog={catalog}
            extra={viewFilters}
          />
        </section>
      )}
    </>
  );
};

const Tile: React.FC<{ label: string; value: string; unit?: string; tone?: string }> = ({ label, value, unit, tone = 'text-stone-900' }) => (
  <div className="px-3 md:px-4 py-2.5 min-w-0">
    <dt className="text-[11px] text-stone-500 truncate">{label}</dt>
    <dd className={`text-[15px] font-semibold tabular-nums truncate ${tone}`} title={unit ? `${value} ${unit}` : value}>
      {value}{unit && <span className="ml-1 text-[11px] font-normal text-stone-500">{unit}</span>}
    </dd>
  </div>
);

export default LedgerTab;
